/**
 * Nutrition screen. Five sub tabs inside #nutritionRoot: log food, meals,
 * hydration, analytics and AI insights. Photos are shrunk in the browser before
 * they go up so a phone picture does not blow the request limit, and whatever
 * the model reads off a plate lands in an editable form, so the player fixes it
 * before anything is saved. Everything talks to /api/nutrition.
 *
 * Log food carries a Food or Drink switch, because a drink is something you had,
 * not a separate chore. A drink logged there lands in the same Meals history as
 * food and in the Hydration ring, and a drink the player names themselves is
 * remembered and offered back. Drinks edit, save and delete exactly like meals.
 *
 * The screen is meant to be looked at, not only filled in. A meal with a photo
 * shows the photo; a meal without one gets a mark drawn from its own macro
 * split, so the list never reads as a column of grey boxes. Today's numbers sit
 * next to the player's OWN usual day, worked out from their own history, and
 * every chart under Analytics is inline SVG written here, no library.
 *
 * The line this screen does not cross: it is fuelling for training, never a
 * diet. No targets, no goal weight, no verdict on a food, nothing medical. Every
 * comparison on screen is the player against themselves, and anything about
 * health goes to a doctor. Said plainly, once, on the surfaces that need it.
 */

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const TABS = [['log', 'Log food'], ['meals', 'Meals'], ['hydration', 'Hydration'], ['analytics', 'Analytics'], ['insights', 'AI insights']];
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack', 'drink', 'supplement'];
const DRINK_KINDS = ['water', 'sports', 'juice', 'milk', 'soda', 'coffee', 'tea', 'other'];
// key, label, unit, sane ceiling for the input
const MACROS = [
  ['calories', 'Calories', 'kcal', 10000], ['protein', 'Protein', 'g', 2000], ['carbs', 'Carbs', 'g', 2000],
  ['fat', 'Fat', 'g', 2000], ['fiber', 'Fiber', 'g', 500], ['sugar', 'Sugar', 'g', 2000], ['sodium', 'Sodium', 'mg', 20000],
];
/** The three macros carry a fixed colour everywhere on the screen, so a ring, a
 *  stacked bar and a legend all mean the same thing. It is a key, not a verdict:
 *  no food on this screen is ever coloured good or bad. */
const SPLIT = [['protein', 'Protein', 'var(--hot)'], ['carbs', 'Carbs', 'var(--cool)'], ['fat', 'Fat', 'var(--grape)']];
const TYPE_MARK = { breakfast: 'BR', lunch: 'LU', dinner: 'DI', snack: 'SN', drink: 'DR', supplement: 'SU' };
const RANGES = [[7, '7 days'], [14, '14 days'], [30, '30 days'], [90, '90 days']];

const n0 = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const clamp = (v, hi) => Math.max(0, Math.min(hi, Math.round(n0(v))));
const fmt = (v) => Math.round(n0(v)).toLocaleString();
const d2 = (n) => String(n).padStart(2, '0');
const localDay = (d) => `${d.getFullYear()}-${d2(d.getMonth() + 1)}-${d2(d.getDate())}`;
const inputValue = (d) => `${localDay(d)}T${d2(d.getHours())}:${d2(d.getMinutes())}`;
const timeOf = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const cut = (s, n) => (String(s).length > n ? `${String(s).slice(0, n - 1)}...` : String(s));
const median = (nums) => {
  const a = nums.slice().sort((x, y) => x - y);
  if (!a.length) return 0;
  const m = a.length >> 1;
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};
/** The counted millilitres a day: water in full, everything else at eighty percent. */
const counted = (row) => n0(row?.effectiveMl != null ? row.effectiveMl : row?.ml);

function dayLabel(key) {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (key === localDay(today)) return 'Today';
  if (key === localDay(yest)) return 'Yesterday';
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
const shortDay = (key) => {
  if (!key) return '';
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const guessType = (d) => { const h = d.getHours(); return h < 11 ? 'breakfast' : h < 15 ? 'lunch' : h < 21 ? 'dinner' : 'snack'; };
const hourLabel = (h) => (h === 0 ? '12am' : h === 12 ? '12pm' : h < 12 ? `${h}am` : `${h - 12}pm`);

/* ------------------------------------------------------------------ state
 * Module level, so the sub tab, the filters and a half typed meal all survive
 * a re render and a trip to another view. */
let ctx = null, root = null, panel = null, tabsEl = null, remTimer = null;
const S = {
  tab: 'log', data: null, err: null,
  filters: { from: '', to: '', type: '' },
  draft: null, editingId: null, photo: null, hint: '',
  // Analytics is a chosen range. Everything fetched for a range is cached under
  // it and thrown away together whenever the player writes something.
  range: 30, an: {}, rangeMeals: {}, sessions: null,
  insights: null, insightsErr: null, factsTried: false,
  drinkKind: 'water', customKind: '',
  // Log food is Food or Drink. The drink form keeps its own half typed draft.
  logMode: 'food', drink: null, editingDrinkId: null,
};

/** A saved meal or drink changes every total, so every cached range goes. */
function forgetRanges() { S.an = {}; S.rangeMeals = {}; S.factsTried = false; }

/* ------------------------------------------------------------------ boot */

export function mount(c) {
  ctx = c;
  root = document.getElementById('nutritionRoot');
  if (!root) return;
  root.innerHTML = `
    <div class="ntabs">${TABS.map(([id, label]) =>
      `<button data-tab="${id}">${esc(label)}</button>`).join('')}</div>
    <div class="nstrip" id="nutToday"></div>
    <div id="nutPanel"></div>
    <div class="modal" id="nutModal"><div class="sheet" id="nutSheet"></div></div>`;
  panel = root.querySelector('#nutPanel');
  tabsEl = root.querySelector('.ntabs');
  tabsEl.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-tab]');
    if (!b) return;
    captureDraft();
    S.tab = b.dataset.tab;
    paintTabs();
    paint();
  });
  root.querySelector('#nutModal').addEventListener('click', (e) => { if (e.target.id === 'nutModal') closeModal(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeModal(); });
  startReminders();
  paintTabs();
}

export async function render() {
  if (!root) return;
  paintTabs();
  if (!S.data) paint();      // never leave the panel blank while the first call is out
  await refresh();
}

/**
 * Every call whose answer depends on what day it is carries the browser's UTC
 * offset. The Worker cannot know where the player is, so without it the day
 * rolls over at UTC midnight and today's totals reset in the middle of the
 * evening anywhere west of London.
 */
function nurl(path, params) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) if (v) q.set(k, v);
  q.set('tz', String(new Date().getTimezoneOffset()));
  return `/api/nutrition${path}?${q}`;
}

async function refresh() {
  try {
    // The filters ride on every call, not only the ones fired from the Meals
    // tab. A refresh from anywhere else came back unfiltered, so Meals then
    // listed meals and drinks the filter row on screen says are hidden.
    const filtered = S.filters.from || S.filters.to || S.filters.type;
    S.data = await ctx.api(nurl('', filtered ? S.filters : null));
    S.err = null;
  } catch (e) { S.err = e.message; }
  paint();
  warmBaseline();
}

/**
 * The strip compares today with the player's own usual day, which needs their
 * last thirty days. It lands after the first paint and repaints only the strip,
 * so nothing on screen jumps while it is in flight.
 */
async function warmBaseline() {
  if (S.an[30] || !S.data) return;
  try { S.an[30] = await ctx.api(nurl('/analytics', { days: 30 })); } catch { return; }
  paintToday();
}

/* ------------------------------------------------------------------ paint */

function paintTabs() {
  if (!tabsEl) return;
  tabsEl.querySelectorAll('button[data-tab]').forEach((b) => b.classList.toggle('on', b.dataset.tab === S.tab));
}

function paint() {
  if (!panel) return;
  paintToday();
  if (S.err) {
    panel.innerHTML = `<div class="card"><h3>Nutrition</h3><p class="sub">${esc(S.err)}</p>
      <button class="btn" id="nutRetry" style="margin-top:12px">Try again</button></div>`;
    bind('#nutRetry', 'click', refresh);
    return;
  }
  if (!S.data) { panel.innerHTML = '<div class="card"><h3>Nutrition</h3><p class="sub">Loading what you have eaten and drunk.</p></div>'; return; }
  const fn = { log: paintLog, meals: paintMeals, hydration: paintHydration, analytics: paintAnalytics, insights: paintInsights }[S.tab] || paintLog;
  try {
    const out = fn();
    if (out && typeof out.catch === 'function') out.catch(failed);
  } catch (e) { failed(e); }
}

function failed(e) {
  if (panel) panel.innerHTML = `<div class="card"><h3>Nutrition</h3><p class="sub">${esc(e?.message || 'That did not load.')}</p></div>`;
}

const bind = (sel, ev, fn, scope = panel) => scope?.querySelectorAll(sel).forEach((el) => el.addEventListener(ev, fn));

/* ============================================================ TODAY STRIP
 * The same block above every tab: what today adds up to, drawn rather than
 * listed, and put next to the player's own usual day. Never next to a
 * recommended intake, because this screen does not set targets. */

/**
 * The middle of their own logged days over the last thirty, today left out.
 * Null until there are three of them, and then the strip says so instead of
 * guessing at a comparison off one day.
 */
function usualDay() {
  const rows = (S.an[30]?.days || []).filter((d) => d && d.date !== localDay(new Date()));
  const live = rows.filter((d) => n0(d.calories) > 0 || n0(d.meals) > 0 || n0(d.ml) > 0);
  if (live.length < 3) return null;
  const mid = (k) => Math.round(median(live.map((d) => n0(d[k]))));
  return {
    days: live.length,
    calories: mid('calories'), protein: mid('protein'), carbs: mid('carbs'), fat: mid('fat'),
    ml: Math.round(median(live.map(counted))),
  };
}

function paintToday() {
  const el = root?.querySelector('#nutToday');
  if (!el) return;
  const t = S.data?.today;
  if (!t) { el.innerHTML = ''; return; }
  const goal = n0(t.goalMl) || n0(S.data.goalMl) || 2500;
  const eff = n0(t.effectiveMl != null ? t.effectiveMl : t.ml);
  const pct = Math.max(0, Math.min(100, t.pct != null ? n0(t.pct) : (goal ? (eff / goal) * 100 : 0)));
  const usual = usualDay();
  const slices = SPLIT.map(([k, label, colour]) => ({ value: n0(t[k]), colour, label }));
  const gram = slices.reduce((a, s) => a + s.value, 0);

  el.innerHTML = `
    <div class="card ntoday">
      <h3>Today</h3>
      <div class="nhero">
        <div class="nhero-cal">
          <b>${fmt(t.calories)}</b>
          <small>calories logged today</small>
          <span class="nvs">${usual
            ? `your usual day is ${fmt(usual.calories)}`
            : 'your usual day shows up after three logged days'}</span>
        </div>
        <div class="nhero-art">
          ${donutSvg(slices, { size: 112, stroke: 15, mid: gram > 0 ? `${Math.round((slices[0].value / gram) * 100)}%` : '', sub: gram > 0 ? 'PROTEIN' : 'NO MACROS', label: "Today's macro split" })}
          <div class="nlegend">${SPLIT.map(([k, label, colour]) =>
            `<span><i style="background:${colour}"></i>${esc(label)} ${fmt(t[k])} g</span>`).join('')}</div>
        </div>
        <div class="nhero-water">
          ${bottleSvg(pct)}
          <small><b>${fmt(eff)}</b> of ${fmt(goal)} ml counted</small>
        </div>
      </div>
      <div class="ncmp">
        ${[['calories', 'Calories', 'kcal', 'var(--hot)'], ['protein', 'Protein', 'g', 'var(--hot)'], ['carbs', 'Carbs', 'g', 'var(--cool)'],
           ['fat', 'Fat', 'g', 'var(--grape)'], ['water', 'Water counted', 'ml', 'var(--cool)']]
          .map(([k, label, unit, colour]) => compareRow(label, unit, colour, k === 'water' ? eff : n0(t[k]), usual ? (k === 'water' ? usual.ml : usual[k]) : null)).join('')}
      </div>
      <p class="nplain">${usual
        ? `Your usual day is the middle of the ${usual.days} days you logged in the last month, so every bar here is your own log next to itself, never a target.`
        : 'Once you have three logged days, every number here sits next to your own usual day.'}
        This is about fuelling your training, not a diet. Anything about your health, your weight or how you feel goes to a parent, a coach or a doctor.</p>
    </div>`;
}

/**
 * Today as a bar, with a notch where their own usual day sits. No target line.
 * The bar carries the same colour the legend above gives that macro, or the
 * key underneath it says one thing and the bars say another.
 */
function compareRow(label, unit, colour, today, usual) {
  const top = Math.max(today, n0(usual)) * 1.25 || 1;
  const w = Math.max(0, Math.min(100, (today / top) * 100));
  const at = usual == null ? null : Math.max(0, Math.min(100, (n0(usual) / top) * 100));
  return `<div class="ncmp-row">
    <div class="ncmp-h"><span>${esc(label)}</span><span><b>${fmt(today)}</b> ${esc(unit)}${
      usual == null ? '' : `, usual <b>${fmt(usual)}</b>`}</span></div>
    <div class="ncmp-bar"><i style="width:${w.toFixed(1)}%;background:${colour}"></i>${
      at == null ? '' : `<u style="left:${at.toFixed(1)}%" title="Your usual day: ${fmt(usual)} ${esc(unit)}"></u>`}</div>
  </div>`;
}

/**
 * A ring built from slices. Used for the macro split, for the mark on a meal
 * with no photo, and for the split by meal type under Analytics.
 */
function donutSvg(slices, { size = 112, stroke = 15, mid = '', sub = '', label = '', hidden = false } = {}) {
  const c2 = size / 2;
  const r = (size - stroke) / 2 - 1;
  const circ = 2 * Math.PI * r;
  const total = slices.reduce((a, s) => a + Math.max(0, n0(s.value)), 0);
  let run = 0;
  const arcs = total > 0 ? slices.map((s) => {
    const len = (Math.max(0, n0(s.value)) / total) * circ;
    if (len <= 0) return '';
    const el = `<circle cx="${c2}" cy="${c2}" r="${r}" fill="none" style="stroke:${s.colour}" stroke-width="${stroke}"
      stroke-dasharray="${len.toFixed(2)} ${Math.max(0, circ - len).toFixed(2)}" stroke-dashoffset="${(-run).toFixed(2)}"
      transform="rotate(-90 ${c2} ${c2})"></circle>`;
    run += len;
    return el;
  }).join('') : '';
  const text = `${mid ? `<text x="${c2}" y="${c2 + (sub ? 1 : 5)}" text-anchor="middle" style="fill:var(--ink);font:800 ${Math.round(size / 6)}px var(--mono)">${esc(mid)}</text>` : ''}
    ${sub ? `<text x="${c2}" y="${c2 + Math.round(size / 6) + 2}" text-anchor="middle" style="fill:var(--mute);font:800 ${Math.max(7, Math.round(size / 13))}px var(--mono)">${esc(sub)}</text>` : ''}`;
  return `<svg class="ndonut" viewBox="0 0 ${size} ${size}" ${hidden ? 'aria-hidden="true"' : `role="img" aria-label="${esc(label || 'Split')}"`}>
    <circle cx="${c2}" cy="${c2}" r="${r}" fill="none" style="stroke:var(--line)" stroke-width="${stroke}"></circle>
    ${arcs}${text}
  </svg>`;
}

/** A bottle that fills. The water level is in the attributes so it is right even
 *  if the wave animation never runs. */
function bottleSvg(pct) {
  const p = Math.max(0, Math.min(100, n0(pct)));
  const top = 34, bottom = 150, span = bottom - top;
  const level = bottom - (span * p) / 100;
  return `<svg class="nbottle" viewBox="0 0 90 160" role="img" aria-label="${Math.round(p)} percent of today's water goal">
    <defs>
      <clipPath id="nbottleClip">
        <path d="M33 6h24v14c0 5 3 7 7 11 6 6 9 12 9 21v92c0 8-5 13-13 13H30c-8 0-13-5-13-13V52c0-9 3-15 9-21 4-4 7-6 7-11V6z"></path>
      </clipPath>
    </defs>
    <g clip-path="url(#nbottleClip)">
      <rect x="0" y="0" width="90" height="160" style="fill:var(--panel2)"></rect>
      ${p > 0 ? `<rect x="0" y="${level.toFixed(1)}" width="90" height="${(bottom - level + 12).toFixed(1)}" style="fill:var(--cool)" opacity=".85"></rect>
        <g class="nwave"><path d="M-90 4 q 22.5 -8 45 0 t 45 0 t 45 0 t 45 0 t 45 0 v 20 h -270 z"
          transform="translate(0 ${(level - 4).toFixed(1)})" style="fill:var(--cool)" opacity=".55"></path></g>` : ''}
    </g>
    <path d="M33 6h24v14c0 5 3 7 7 11 6 6 9 12 9 21v92c0 8-5 13-13 13H30c-8 0-13-5-13-13V52c0-9 3-15 9-21 4-4 7-6 7-11V6z"
      fill="none" style="stroke:var(--line)" stroke-width="3"></path>
    <text x="45" y="${p > 55 ? 120 : 96}" text-anchor="middle" style="fill:var(--ink);font:800 20px var(--mono)">${Math.round(p)}%</text>
  </svg>`;
}

/* ============================================================== 1. LOG FOOD */

function paintLog() {
  panel.innerHTML = `
    <div class="card">
      <h3>What are you logging</h3>
      <div class="ntabs" id="nfMode" style="margin-bottom:0">
        <button data-mode="food" class="${S.logMode === 'drink' ? '' : 'on'}">Food</button>
        <button data-mode="drink" class="${S.logMode === 'drink' ? 'on' : ''}">Drink</button>
      </div>
    </div>
    ${S.logMode === 'drink' ? drinkFormHtml() : foodFormHtml()}`;

  bind('#nfMode button', 'click', (e) => {
    const mode = e.currentTarget.dataset.mode;
    if (mode === S.logMode) return;
    captureDraft();                     // keep whatever is half typed on the other side
    S.logMode = mode;
    paint();
  });
  if (S.logMode === 'drink') bindDrinkForm(); else bindFoodForm();
}

function foodFormHtml() {
  const d = S.draft || {};
  const preview = S.photo?.dataUrl || S.photo?.url;
  return `
    <div class="ngrid">
      <div class="card">
        <h3>Photo of your food</h3>
        <label class="nfile">
          <input type="file" id="nfPhoto" accept="image/*" capture="environment">
          <b>Take a photo, or pick one</b>
          <small>It gets shrunk on this device before it goes anywhere.</small>
        </label>
        <div class="npreview" id="nfPreview">${preview ? `<img src="${esc(preview)}" alt="The food you are logging">` : ''}</div>
        <label class="nlab">What is in it, if you know
          <input type="text" id="nfHint" maxlength="200" placeholder="chicken and rice" value="${esc(S.hint)}">
        </label>
        <button class="btn primary wide" id="nfAnalyze" ${S.photo?.dataUrl ? '' : 'disabled'} style="margin-top:12px">Analyze this photo</button>
        <div class="nprog" id="nfProg"></div>
      </div>

      <div class="card">
        <h3>${S.editingId ? 'Edit this meal' : 'The details'}</h3>
        ${d.source === 'photo' ? `
          <div class="nnote">
            <span class="pill ${confPill(d.confidence)}">Confidence ${confText(d.confidence)}</span>
            <p>Your companion read this off a photo, so it can be wrong. Correct anything that looks off before you save.</p>
            ${d.photoNote ? `<p class="sub">${esc(d.photoNote)}</p>` : ''}
          </div>` : ''}
        <div class="nform">
          <label class="nlab wide">Name<input type="text" id="nfName" maxlength="120" placeholder="Chicken, rice and broccoli" value="${esc(d.name || '')}"></label>
          <label class="nlab">Type<select id="nfType">${MEAL_TYPES.map((t) =>
            `<option value="${t}" ${(d.type || guessType(new Date())) === t ? 'selected' : ''}>${t[0].toUpperCase()}${t.slice(1)}</option>`).join('')}</select></label>
          <label class="nlab">When<input type="datetime-local" id="nfAt" value="${inputValue(d.at ? new Date(d.at) : new Date())}"></label>
          <label class="nlab wide">Ingredients, separated by commas<input type="text" id="nfIng" maxlength="600" placeholder="chicken breast, white rice, broccoli" value="${esc((d.ingredients || []).join(', '))}"></label>
          ${MACROS.map(([k, label, unit, hi]) =>
            `<label class="nlab">${label} <em>${unit}</em><input type="number" id="nf_${k}" min="0" max="${hi}" step="1" value="${d[k] == null ? '' : clamp(d[k], hi)}"></label>`).join('')}
          <label class="nlab wide">Note<input type="text" id="nfNote" maxlength="500" placeholder="ate an hour before practice" value="${esc(d.note || '')}"></label>
        </div>
        <div class="nactions">
          <button class="btn primary" id="nfSave">${S.editingId ? 'Save changes' : 'Save this meal'}</button>
          <button class="btn" id="nfReset">${S.editingId ? 'Cancel edit' : 'Clear the form'}</button>
        </div>
        <p class="sub" style="margin-top:10px">Nothing is saved until you press save. Typing it in by hand works the same way, the form starts empty and the time is set to now.</p>
      </div>
    </div>`;
}

function bindFoodForm() {
  bind('#nfPhoto', 'change', onPhoto);
  bind('#nfAnalyze', 'click', analyze);
  bind('#nfSave', 'click', saveMeal);
  bind('#nfReset', 'click', () => { S.draft = null; S.editingId = null; S.photo = null; S.hint = ''; paint(); });
  bind('#nfHint', 'input', (e) => { S.hint = e.target.value; });
}

/** Reads whatever is in the open form into its draft, so a tab switch does not lose it. */
function captureDraft() {
  if (S.tab !== 'log') return;
  if (panel?.querySelector('#dkMl')) { S.drink = readDrinkForm(); return; }
  if (!panel?.querySelector('#nfName')) return;
  const f = readForm();
  const typed = Boolean(f.name || f.ingredients.length || f.note) || MACROS.some(([k]) => f[k] > 0);
  if (typed || S.draft) S.draft = { ...(S.draft || {}), ...f };
}

const confNumber = (c) => { const v = n0(c); return v > 1 ? v / 100 : v; };
const confPill = (c) => { const v = confNumber(c); return v >= 0.75 ? 'good' : v >= 0.5 ? 'cool' : 'hot'; };
const confText = (c) => (c == null ? 'not given' : `${Math.round(confNumber(c) * 100)} percent`);

/** Longest side to about 1280, jpeg at 0.82. A phone photo lands around 200 KB. */
async function shrink(file, maxSide = 1280, quality = 0.82) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error('That file did not open as an image.'));
      i.src = url;
    });
    const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * scale));
    c.height = Math.max(1, Math.round(h * scale));
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', quality);
  } finally { URL.revokeObjectURL(url); }
}

async function onPhoto(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  const box = panel.querySelector('#nfPreview');
  if (box) box.innerHTML = '<p class="sub">Shrinking the photo on this device.</p>';
  try {
    const dataUrl = await shrink(file);
    S.photo = { dataUrl };
    // A new photo invalidates whatever the last analyze put in R2, or the meal
    // would save with the picture the player just replaced.
    if (S.draft && !S.editingId) S.draft.photo = null;
    if (box) box.innerHTML = `<img src="${dataUrl}" alt="The food you are logging">`;
    const btn = panel.querySelector('#nfAnalyze');
    if (btn) btn.disabled = false;
  } catch (err) {
    S.photo = null;
    if (box) box.innerHTML = `<p class="sub">${esc(err.message)}</p>`;
  }
}

async function analyze() {
  if (!S.photo?.dataUrl) return ctx.toast('Take or pick a photo first.', true);
  const btn = panel.querySelector('#nfAnalyze');
  const box = panel.querySelector('#nfProg');
  const step = (text, pct) => { if (box) box.innerHTML = `<div class="nprog-t">${esc(text)}</div><div class="bar"><i style="width:${pct}%"></i></div>`; };
  if (btn) { btn.disabled = true; btn.textContent = 'Reading the photo'; }
  const t0 = Date.now();
  step('Sending the photo to your companion.', 20);
  const tick = setInterval(() => {
    const s = Math.round((Date.now() - t0) / 1000);
    step(`Your companion is reading the plate. ${s} second${s === 1 ? '' : 's'} so far.`, Math.min(92, 20 + s * 7));
  }, 1000);
  try {
    const hint = (panel.querySelector('#nfHint')?.value || '').trim().slice(0, 200);
    S.hint = hint;
    const r = await ctx.api('/api/nutrition/analyze', {
      method: 'POST',
      body: JSON.stringify(hint ? { image: S.photo.dataUrl, hint } : { image: S.photo.dataUrl }),
    });
    clearInterval(tick);
    // The model's sentence gets its own key. `note` belongs to the player, so a
    // tab switch that reads the form back cannot wipe what the companion saw.
    const { note: seen, ...rest } = r;
    S.draft = {
      ...rest,
      photoNote: seen,
      ingredients: Array.isArray(r.ingredients) ? r.ingredients : [],
      at: new Date().toISOString(),
      type: MEAL_TYPES.includes(r.type) ? r.type : guessType(new Date()),
      source: 'photo',
    };
    S.editingId = null;
    paint();
    ctx.toast('Read it. Check the numbers before you save.');
  } catch (e) {
    clearInterval(tick);
    step(`That did not work: ${e.message}`, 0);
    if (btn) { btn.disabled = false; btn.textContent = 'Analyze this photo'; }
  }
}

function readForm() {
  const v = (id) => panel.querySelector(`#${id}`)?.value ?? '';
  const at = v('nfAt');
  const body = {
    name: v('nfName').trim().slice(0, 120),
    type: MEAL_TYPES.includes(v('nfType')) ? v('nfType') : 'snack',
    at: at ? new Date(at).toISOString() : new Date().toISOString(),
    ingredients: v('nfIng').split(',').map((x) => x.trim().slice(0, 60)).filter(Boolean).slice(0, 40),
    note: v('nfNote').trim().slice(0, 500),
  };
  for (const [k, , , hi] of MACROS) body[k] = clamp(v(`nf_${k}`), hi);
  if (S.draft?.photo) body.photo = S.draft.photo;
  // Part of the meal record, and the detail sheet shows it back.
  if (S.draft?.confidence != null) body.confidence = S.draft.confidence;
  return body;
}

async function saveMeal() {
  const body = readForm();
  if (!body.name) return ctx.toast('Give it a name first.', true);
  const btn = panel.querySelector('#nfSave');
  if (btn) btn.disabled = true;
  try {
    if (S.editingId) await ctx.api(`/api/nutrition/meals/${encodeURIComponent(S.editingId)}`, { method: 'PATCH', body: JSON.stringify(body) });
    else await ctx.api('/api/nutrition/meals', { method: 'POST', body: JSON.stringify(body) });
    ctx.toast(S.editingId ? 'Meal updated.' : 'Meal saved.');
    S.draft = null; S.editingId = null; S.photo = null; S.hint = '';
    forgetRanges();
    S.tab = 'meals';
    paintTabs();
    await refresh();
  } catch (e) {
    ctx.toast(e.message, true);
    if (btn) btn.disabled = false;
  }
}

/* ----------------------------------------------------- 1b. LOG A DRINK
 * Same screen as food, because a drink is something you had, not a separate
 * chore. What saves here is the same record the Hydration ring adds. */

const customDrinks = () => (Array.isArray(S.data?.customDrinks) ? S.data.customDrinks : []);

/** The select only holds these. A remembered drink they just forgot falls back to water. */
const validKind = (kind) => (kind === 'custom' || DRINK_KINDS.includes(kind) || customDrinks().includes(kind) ? kind : 'water');

function drinkOptions(selected) {
  const rows = [
    ...DRINK_KINDS.map((k) => [k, `${k[0].toUpperCase()}${k.slice(1)}`]),
    ...customDrinks().map((c) => [c, c]),
    ['custom', 'Something else'],
  ];
  return rows.map(([value, label]) =>
    `<option value="${esc(value)}" ${value === selected ? 'selected' : ''}>${esc(label)}</option>`).join('');
}

/** A saved drink turned back into what the form holds. */
function drinkFormFrom(d) {
  const kind = String(d?.kind || 'water');
  const label = String(d?.label || '');
  if (kind !== 'other') return { kind: validKind(kind), custom: '', ml: n0(d?.ml), at: d?.at || new Date().toISOString() };
  const remembered = customDrinks().find((c) => c.toLowerCase() === label.toLowerCase());
  return { kind: remembered || 'custom', custom: label, ml: n0(d?.ml), at: d?.at || new Date().toISOString() };
}

function drinkFormHtml() {
  const d = { kind: 'water', custom: '', ml: 250, at: new Date().toISOString(), ...(S.drink || {}) };
  // A drink they forgot about while the form was open keeps its typed name.
  if (validKind(d.kind) !== d.kind) d.kind = d.custom ? 'custom' : 'water';
  const customs = customDrinks();
  return `
    <div class="card">
      <h3>${S.editingDrinkId ? 'Edit this drink' : 'Log a drink'}</h3>
      <div class="nform">
        <label class="nlab">Drink<select id="dkKind">${drinkOptions(d.kind)}</select></label>
        <label class="nlab" id="dkCustomWrap" ${d.kind === 'custom' ? '' : 'hidden'}>Call it<input type="text" id="dkCustom" maxlength="40" placeholder="Gatorade Zero" value="${esc(d.custom)}"></label>
        <label class="nlab">Amount <em>ml</em><input type="number" id="dkMl" min="1" max="3000" step="10" value="${clamp(d.ml, 3000) || 250}"></label>
        <label class="nlab">When<input type="datetime-local" id="dkAt" value="${inputValue(d.at ? new Date(d.at) : new Date())}"></label>
      </div>
      <div class="nactions">
        <button class="btn" data-add="250">Add 250 ml</button>
        <button class="btn" data-add="500">Add 500 ml</button>
      </div>
      ${customs.length ? `
        <h3 style="margin-top:20px">Drinks you named yourself</h3>
        <div class="log nlog">${customs.map((c) => `<span class="chip">
          <button data-pick="${esc(c)}">${esc(c)}</button>
          <button data-forget="${esc(c)}" aria-label="Forget ${esc(c)}" title="Forget ${esc(c)}" style="margin-left:8px;color:var(--mute)">&times;</button>
        </span>`).join('')}</div>
        <p class="sub" style="margin-top:8px">Tap one to log it again, or the cross to stop it being offered.</p>` : ''}
      <div class="nactions">
        <button class="btn primary" id="dkSave">${S.editingDrinkId ? 'Save changes' : 'Save this drink'}</button>
        <button class="btn" id="dkReset">${S.editingDrinkId ? 'Cancel edit' : 'Clear the form'}</button>
      </div>
      <p class="nplain">Water counts fully toward your goal. Everything else counts eighty percent. Anything you save here shows up in Meals and in Hydration, and you can edit or delete it after.</p>
    </div>`;
}

function bindDrinkForm() {
  bind('#dkKind', 'change', () => {
    S.drink = readDrinkForm();
    const wrap = panel.querySelector('#dkCustomWrap');
    if (wrap) wrap.hidden = S.drink.kind !== 'custom';
  });
  bind('[data-add]', 'click', (e) => {
    const el = panel.querySelector('#dkMl');
    if (!el) return;
    el.value = String(Math.min(3000, clamp(el.value, 3000) + n0(e.currentTarget.dataset.add)));
    S.drink = readDrinkForm();
  });
  bind('[data-pick]', 'click', (e) => { S.drink = { ...readDrinkForm(), kind: e.currentTarget.dataset.pick }; paint(); });
  bind('[data-forget]', 'click', (e) => forgetCustom(e.currentTarget.dataset.forget));
  bind('#dkSave', 'click', saveDrink);
  bind('#dkReset', 'click', () => { S.drink = null; S.editingDrinkId = null; paint(); });
}

function readDrinkForm() {
  const v = (id) => panel.querySelector(`#${id}`)?.value ?? '';
  const at = v('dkAt');
  return {
    kind: v('dkKind') || 'water',
    custom: v('dkCustom').trim().slice(0, 40),
    ml: clamp(v('dkMl'), 3000),
    at: at ? new Date(at).toISOString() : new Date().toISOString(),
  };
}

async function saveDrink() {
  const f = readDrinkForm();
  const kind = f.kind === 'custom' ? f.custom : f.kind;
  if (!kind) return ctx.toast('Give your drink a name first.', true);
  if (!f.ml) return ctx.toast('Put in how much you drank.', true);
  const btn = panel.querySelector('#dkSave');
  if (btn) btn.disabled = true;
  const body = JSON.stringify({ ml: f.ml, kind, at: f.at });
  try {
    const r = S.editingDrinkId
      ? await ctx.api(nurl(`/drinks/${encodeURIComponent(S.editingDrinkId)}`), { method: 'PATCH', body })
      : await ctx.api(nurl('/drinks'), { method: 'POST', body });
    applyDrinks(r);
    ctx.toast(S.editingDrinkId ? 'Drink updated.' : `Logged ${f.ml} ml.`);
    S.drink = null; S.editingDrinkId = null;
    forgetRanges();
    S.tab = 'meals';
    paintTabs();
    await refresh();
  } catch (e) {
    ctx.toast(e.message, true);
    if (btn) btn.disabled = false;
  }
}

async function forgetCustom(label) {
  if (!label) return;
  try {
    const ok = await ctx.ui.confirm({
      title: `Forget ${label}?`,
      body: 'It stops being offered here. Drinks you already logged under that name stay in your history.',
      confirmText: 'Forget it', danger: true,
    });
    if (!ok) return;
    const r = await ctx.api(nurl('/customdrinks', { label }), { method: 'DELETE' });
    if (S.data && Array.isArray(r?.customDrinks)) S.data.customDrinks = r.customDrinks;
    const form = panel.querySelector('#dkMl') ? readDrinkForm() : (S.drink || null);
    if (form && form.kind === label) form.kind = 'water';
    S.drink = form;
    ctx.toast(`Forgot ${label}.`);
    paint();
  } catch (e) { ctx.toast(e.message, true); }
}

/* ================================================================ 2. MEALS */

function photoUrl(meal) {
  if (!meal?.photo) return null;
  const seg = String(meal.photo).split('/').pop() || '';
  return `/api/nutrition/photo/${encodeURIComponent(seg.replace(/\.[a-z0-9]+$/i, ''))}`;
}
// The photo route takes an id; if it wants the file name instead, the thumb retries with it.
function photoUrlAlt(meal) {
  const seg = String(meal?.photo || '').split('/').pop() || '';
  return `/api/nutrition/photo/${encodeURIComponent(seg)}`;
}
/**
 * A photo that will not load falls back to the mark drawn underneath it rather
 * than a hole in the list, so every row always shows something.
 */
function bindThumbs(scope) {
  scope?.querySelectorAll('img[data-alt]').forEach((img) => {
    img.addEventListener('error', () => {
      const alt = img.dataset.alt;
      if (alt && img.src.indexOf(alt) === -1) { img.dataset.alt = ''; img.src = alt; return; }
      img.remove();
    });
  });
}

const ACCENTS = ['var(--hot)', 'var(--cool)', 'var(--grape)', 'var(--good)', 'var(--hot2)'];
const hashOf = (s) => { let h = 0; for (let i = 0; i < String(s).length; i++) h = (h * 31 + String(s).charCodeAt(i)) >>> 0; return h; };

/**
 * A meal with no photo still gets a face: its own macro split as a ring, with
 * the meal type in the middle. Two different meals look different, the same meal
 * always looks the same, and nothing about it says whether the food was good.
 */
function mealMark(meal, size) {
  const slices = SPLIT.map(([k, label, colour]) => ({ value: n0(meal?.[k]), colour, label }));
  const gram = slices.reduce((a, s) => a + s.value, 0);
  const paint = gram > 0 ? slices : [{ value: 1, colour: ACCENTS[hashOf(meal?.name || 'meal') % ACCENTS.length], label: 'no macros' }];
  const mark = TYPE_MARK[meal?.type] || 'ME';
  // Decorative: the row and the detail sheet both say the meal's name and type
  // in text already, so a screen reader does not need to hear the mark too.
  return donutSvg(paint, { size, stroke: Math.max(5, Math.round(size / 7)), mid: mark, sub: '', hidden: true });
}

function mealArt(meal, size, cls) {
  const url = photoUrl(meal);
  return `<span class="${cls}">${mealMark(meal, size)}${
    url ? `<img src="${esc(url)}" data-alt="${esc(photoUrlAlt(meal))}" alt="${esc(meal?.name || 'Meal')}">` : ''}</span>`;
}

/** The macro split of one meal as a hairline under its name. */
function miniSplit(meal) {
  const vals = SPLIT.map(([k]) => n0(meal?.[k]));
  const total = vals.reduce((a, b) => a + b, 0);
  if (total <= 0) return '';
  return `<span class="nmini">${SPLIT.map(([, , colour], i) =>
    `<i style="width:${((vals[i] / total) * 100).toFixed(1)}%;background:${colour}"></i>`).join('')}</span>`;
}

/** One history for both: meals and drinks, newest day first, newest entry first. */
function groupByDay(meals, drinks) {
  const map = new Map();
  const put = (at, row) => {
    const k = localDay(new Date(at));
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(row);
  };
  for (const m of meals) put(m.at, { kind: 'meal', at: m.at, item: m });
  for (const d of drinks) put(d.at, { kind: 'drink', at: d.at, item: d });
  return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, list]) => ({ date, list: list.slice().sort((a, b) => String(b.at).localeCompare(String(a.at))) }));
}
const sumOf = (list, k) => list.reduce((a, x) => a + n0(x[k]), 0);
const pick = (list, kind) => list.filter((r) => r.kind === kind).map((r) => r.item);

function mealRow(m) {
  return `<button class="nrow" data-id="${esc(m.id)}">
    ${mealArt(m, 58, 'nthumb')}
    <span class="nrow-m"><b>${esc(m.name || 'Meal')}</b>
      <small><span class="pill ${m.type === 'snack' ? 'cool' : 'hot'}">${esc(m.type || 'meal')}</span> ${esc(timeOf(m.at))}${
        m.photo ? ' <span class="nflag">photo</span>' : ''}</small>
      ${miniSplit(m)}</span>
    <span class="nrow-k"><b>${fmt(m.calories)}</b><small>kcal</small></span>
  </button>`;
}

function drinkRow(d) {
  const water = d.kind === 'water';
  return `<button class="nrow" data-drink="${esc(d.id)}">
    <span class="nthumb ndrop">${dropSvg(water)}</span>
    <span class="nrow-m"><b>${esc(d.label || d.kind || 'Drink')}</b>
      <small><span class="pill cool">drink</span> ${esc(timeOf(d.at))}</small></span>
    <span class="nrow-k"><b>${fmt(d.ml)}</b><small>ml</small></span>
  </button>`;
}

/** The mark a drink gets in the list: a drop, filled for water, outlined for the rest.
 *  Decorative, because the row already says what the drink was. */
function dropSvg(water) {
  return `<svg viewBox="0 0 48 48" aria-hidden="true">
    <path d="M24 7c8 9 13 15 13 21a13 13 0 0 1-26 0c0-6 5-12 13-21z"
      style="fill:${water ? 'var(--cool)' : 'none'};stroke:var(--cool)" stroke-width="3" opacity="${water ? '.9' : '1'}"></path>
  </svg>`;
}

function paintMeals() {
  const meals = Array.isArray(S.data.meals) ? S.data.meals : [];
  const drinks = Array.isArray(S.data.drinkLog) ? S.data.drinkLog : [];
  const groups = groupByDay(meals, drinks);
  panel.innerHTML = `
    <div class="card">
      <h3>Filter</h3>
      <div class="nfilters">
        <label class="nlab">From<input type="date" id="mfFrom" value="${esc(S.filters.from)}"></label>
        <label class="nlab">To<input type="date" id="mfTo" value="${esc(S.filters.to)}"></label>
        <label class="nlab">Type<select id="mfType"><option value="">Every type</option>${MEAL_TYPES.map((t) =>
          `<option value="${t}" ${S.filters.type === t ? 'selected' : ''}>${t[0].toUpperCase()}${t.slice(1)}</option>`).join('')}</select></label>
        <button class="btn" id="mfClear">Clear</button>
      </div>
      <div class="nlegend" style="margin-top:12px">${SPLIT.map(([, label, colour]) =>
        `<span><i style="background:${colour}"></i>${esc(label)}</span>`).join('')}
        <span class="sub">A meal with no photo is drawn from its own macro split.</span></div>
    </div>
    ${groups.length ? groups.map((g) => {
      const dm = pick(g.list, 'meal'), dd = pick(g.list, 'drink');
      return `
      <div class="card nday">
        <h3>${esc(dayLabel(g.date))}</h3>
        <div class="ndaytot">
          <span><b>${fmt(sumOf(dm, 'calories'))}</b> kcal</span>
          <span><b>${fmt(sumOf(dm, 'protein'))}</b> g protein</span>
          <span><b>${fmt(sumOf(dm, 'carbs'))}</b> g carbs</span>
          <span><b>${fmt(sumOf(dm, 'fat'))}</b> g fat</span>
          <span>${dm.length} meal${dm.length === 1 ? '' : 's'}</span>
          ${dd.length ? `<span><b>${fmt(sumOf(dd, 'ml'))}</b> ml drunk</span>` : ''}
        </div>
        <div class="nrows">${g.list.map((r) => (r.kind === 'drink' ? drinkRow(r.item) : mealRow(r.item))).join('')}</div>
      </div>`; }).join('')
      : `<div class="card"><h3>Meals</h3><p class="sub">${(S.filters.from || S.filters.to || S.filters.type)
          ? 'Nothing matches that filter.' : 'Nothing logged yet. Take a photo of your next meal in Log food, type one in by hand, or switch that screen to Drink.'}</p></div>`}`;

  bind('#mfFrom', 'change', (e) => { S.filters.from = e.target.value; refresh(); });
  bind('#mfTo', 'change', (e) => { S.filters.to = e.target.value; refresh(); });
  bind('#mfType', 'change', (e) => { S.filters.type = e.target.value; refresh(); });
  bind('#mfClear', 'click', () => { S.filters = { from: '', to: '', type: '' }; refresh(); });
  bind('.nrow[data-id]', 'click', (e) => {
    const id = e.currentTarget.dataset.id;
    openMeal(meals.find((m) => String(m.id) === id));
  });
  bind('.nrow[data-drink]', 'click', (e) => {
    const id = e.currentTarget.dataset.drink;
    openDrink(drinks.find((d) => String(d.id) === id));
  });
  bindThumbs(panel);
}

function openMeal(m) {
  if (!m) return;
  const slices = SPLIT.map(([k, label, colour]) => ({ value: n0(m[k]), colour, label }));
  const gram = slices.reduce((a, s) => a + s.value, 0);
  const sheet = root.querySelector('#nutSheet');
  sheet.innerHTML = `
    <h2>${esc(m.name || 'Meal')}</h2>
    <p class="sub">${esc(m.type || 'meal')} &middot; ${esc(new Date(m.at).toLocaleString())}${
      m.source ? ` &middot; ${m.source === 'photo' ? 'from a photo' : 'typed in'}` : ''}</p>
    ${mealArt(m, 150, 'nshot')}
    <section>
      <div class="grid-stats">${MACROS.map(([k, label, unit]) =>
        `<div class="stat"><b>${fmt(m[k])}</b><small>${esc(label)} ${esc(unit)}</small></div>`).join('')}</div>
      ${gram > 0 ? `<div class="nsplit" style="margin-top:14px">${slices.map((s) =>
        `<i style="width:${((s.value / gram) * 100).toFixed(1)}%;background:${s.colour}"></i>`).join('')}</div>
        <div class="nlegend">${slices.map((s) =>
          `<span><i style="background:${s.colour}"></i>${esc(s.label)} ${Math.round((s.value / gram) * 100)}%</span>`).join('')}</div>` : ''}
    </section>
    ${(m.ingredients || []).length ? `<section><h4>Ingredients</h4><div class="log">${
      m.ingredients.map((i) => `<span class="chip">${esc(i)}</span>`).join('')}</div></section>` : ''}
    ${m.note ? `<section><h4>Note</h4><p>${esc(m.note)}</p></section>` : ''}
    ${m.confidence != null ? `<section><h4>How sure your companion was</h4><p><span class="pill ${confPill(m.confidence)}">${confText(m.confidence)}</span></p></section>` : ''}
    <div class="nactions">
      <button class="btn" id="nmClose">Close</button>
      <button class="btn primary" id="nmEdit">Edit</button>
      <button class="btn" id="nmDelete" style="color:var(--bad)">Delete</button>
    </div>`;
  root.querySelector('#nutModal').classList.add('open');
  bindThumbs(sheet);
  sheet.querySelector('#nmClose').addEventListener('click', closeModal);
  sheet.querySelector('#nmEdit').addEventListener('click', () => {
    S.draft = { ...m };
    S.editingId = m.id;
    S.photo = photoUrl(m) ? { url: photoUrl(m) } : null;
    S.hint = '';
    closeModal();
    S.tab = 'log';
    S.logMode = 'food';
    paintTabs();
    paint();
  });
  sheet.querySelector('#nmDelete').addEventListener('click', async () => {
    try {
      const ok = await ctx.ui.confirm({
        title: 'Delete this meal?',
        body: 'It comes out of your log and your companion stops counting it.',
        confirmText: 'Delete', danger: true,
      });
      if (!ok) return;
      await ctx.api(`/api/nutrition/meals/${encodeURIComponent(m.id)}`, { method: 'DELETE' });
      closeModal();
      forgetRanges();
      ctx.toast('Meal deleted.');
      await refresh();
    } catch (e) { ctx.toast(e.message, true); }
  });
}

/** The same sheet a meal opens, for a drink: what it was, how much, edit, delete. */
function openDrink(d) {
  if (!d) return;
  const water = d.kind === 'water';
  const count = Math.round(n0(d.ml) * (water ? 1 : 0.8));
  const sheet = root.querySelector('#nutSheet');
  sheet.innerHTML = `
    <h2>${esc(d.label || d.kind || 'Drink')}</h2>
    <p class="sub">drink &middot; ${esc(new Date(d.at).toLocaleString())}</p>
    <section>
      <div class="grid-stats">
        <div class="stat"><b>${fmt(d.ml)}</b><small>millilitres</small></div>
        <div class="stat"><b>${fmt(count)}</b><small>counted ml</small></div>
      </div>
      <p class="nplain">${water
        ? 'Water counts fully toward your daily goal.'
        : 'Everything that is not water counts eighty percent toward your daily goal.'}</p>
    </section>
    <div class="nactions">
      <button class="btn" id="ndClose">Close</button>
      <button class="btn primary" id="ndEdit">Edit</button>
      <button class="btn" id="ndDelete" style="color:var(--bad)">Delete</button>
    </div>`;
  root.querySelector('#nutModal').classList.add('open');
  sheet.querySelector('#ndClose').addEventListener('click', closeModal);
  sheet.querySelector('#ndEdit').addEventListener('click', () => {
    S.drink = drinkFormFrom(d);
    S.editingDrinkId = d.id;
    closeModal();
    S.tab = 'log';
    S.logMode = 'drink';
    paintTabs();
    paint();
  });
  sheet.querySelector('#ndDelete').addEventListener('click', async () => {
    try {
      const ok = await ctx.ui.confirm({
        title: 'Delete this drink?',
        body: 'It comes out of your log and stops counting toward your water goal.',
        confirmText: 'Delete', danger: true,
      });
      if (!ok) return;
      const r = await ctx.api(nurl(`/drinks/${encodeURIComponent(d.id)}`), { method: 'DELETE' });
      applyDrinks(r);
      closeModal();
      if (S.editingDrinkId === d.id) { S.editingDrinkId = null; S.drink = null; }
      forgetRanges();
      ctx.toast('Drink deleted.');
      await refresh();
    } catch (e) { ctx.toast(e.message, true); }
  });
}

function closeModal() { root?.querySelector('#nutModal')?.classList.remove('open'); }

/* ============================================================ 3. HYDRATION */

async function paintHydration() {
  const t = S.data.today || {};
  const goal = n0(t.goalMl) || n0(S.data.goalMl) || 2500;
  const eff = n0(t.effectiveMl ?? t.ml);
  const pct = Math.max(0, Math.min(100, t.pct != null ? n0(t.pct) : (goal ? (eff / goal) * 100 : 0)));
  const key = localDay(new Date());
  const drinks = (Array.isArray(S.data.drinks) ? S.data.drinks : [])
    .filter((d) => d && localDay(new Date(d.at)) === key)
    .sort((a, b) => String(a.at).localeCompare(String(b.at)));
  const last = drinks[drinks.length - 1];
  const rem = remState();
  // A drink they named themselves is offered here too, so keep the pick honest.
  S.drinkKind = validKind(S.drinkKind);

  panel.innerHTML = `
    <div class="ngrid">
      <div class="card">
        <h3>Today</h3>
        <div class="nringwrap">${ringSvg(pct, eff, goal)}</div>
        <p class="nplain">Water counts fully. Everything else counts eighty percent toward your goal.</p>
        <div class="nquick">
          <label class="nlab">Drink<select id="hyKind">${drinkOptions(S.drinkKind)}</select></label>
          <label class="nlab" id="hyCustomWrap" ${S.drinkKind === 'custom' ? '' : 'hidden'}>Call it<input type="text" id="hyCustom" maxlength="40" placeholder="coconut water" value="${esc(S.customKind)}"></label>
        </div>
        <div class="nactions">
          <button class="btn primary" data-ml="250">Add 250 ml</button>
          <button class="btn primary" data-ml="500">Add 500 ml</button>
          <button class="btn" id="hyUndo" ${last ? '' : 'disabled'}>Undo the last one</button>
        </div>
        ${drinks.length ? `<div class="log nlog">${drinks.slice(-12).map((d) =>
          `<span class="chip">${esc(d.label || d.kind || 'drink')} ${fmt(d.ml)} ml</span>`).join('')}</div>` : '<p class="sub" style="margin-top:12px">Nothing to drink logged yet today.</p>'}
      </div>

      <div class="card">
        <h3>Your goal</h3>
        <div class="nquick">
          <label class="nlab">Millilitres a day<input type="number" id="hyGoal" min="500" max="8000" step="50" value="${goal}"></label>
          <button class="btn" id="hyGoalSave">Save goal</button>
        </div>
        <p class="sub">This box starts at 2,500 ml because the app has to start somewhere. It is not a number we picked for you. Put in whatever you, your family or your coach agreed on, and change it whenever they do.</p>
        <h3 style="margin-top:20px">The last seven days</h3>
        <div id="hyChart"><p class="sub">Loading the last seven days.</p></div>
        <div class="srow" style="margin-top:16px">
          <div class="lab"><b>Drink reminders</b><small>${rem.on
            ? 'On, spaced about every two hours between 8am and 9pm while the app is open.'
            : 'Off. Turn it on and you get a nudge about every two hours between 8am and 9pm.'}</small></div>
          <div class="sw ${rem.on ? 'on' : ''}" id="hyRem"></div>
        </div>
        <p class="sub">${'Notification' in window
          ? 'Turning it on asks your browser for permission. If you say no, the reminder shows inside the app instead.'
          : 'This browser has no notifications, so reminders show inside the app.'}</p>
      </div>
    </div>`;

  bind('#hyKind', 'change', (e) => {
    S.drinkKind = e.target.value;
    const wrap = panel.querySelector('#hyCustomWrap');
    if (wrap) wrap.hidden = S.drinkKind !== 'custom';
  });
  bind('#hyCustom', 'input', (e) => { S.customKind = e.target.value; });
  bind('[data-ml]', 'click', (e) => addDrink(Number(e.currentTarget.dataset.ml)));
  bind('#hyUndo', 'click', () => undoDrink(last));
  bind('#hyGoalSave', 'click', saveGoal);
  bind('#hyRem', 'click', toggleReminders);

  await loadHydroChart();
}

function ringSvg(pct, eff, goal) {
  const r = 54, c = 2 * Math.PI * r, on = (Math.max(0, Math.min(100, pct)) / 100) * c;
  // A round cap on a zero length arc still paints a dot, which reads as progress
  // nobody made. Nothing drunk, nothing drawn.
  const arc = on > 0 ? `<circle cx="75" cy="75" r="${r}" fill="none" style="stroke:var(--cool)" stroke-width="15" stroke-linecap="round"
      stroke-dasharray="${on.toFixed(1)} ${Math.max(0, c - on).toFixed(1)}" transform="rotate(-90 75 75)"></circle>` : '';
  return `<svg viewBox="0 0 150 150" class="nring" role="img" aria-label="${Math.round(pct)} percent of today's hydration goal">
    <circle cx="75" cy="75" r="${r}" fill="none" style="stroke:var(--line)" stroke-width="15"></circle>
    ${arc}
    <text x="75" y="72" text-anchor="middle" style="fill:var(--ink);font:800 24px var(--mono)">${fmt(eff)}</text>
    <text x="75" y="92" text-anchor="middle" style="fill:var(--mute);font:700 11px var(--mono)">OF ${fmt(goal)} ML</text>
    <text x="75" y="112" text-anchor="middle" style="fill:var(--cool);font:800 13px var(--mono)">${Math.round(pct)}%</text>
  </svg>`;
}

function drinkKind() {
  if (S.drinkKind !== 'custom') return S.drinkKind;
  const label = (S.customKind || '').trim().slice(0, 40);
  return label || 'other';
}

async function addDrink(ml) {
  if (!Number.isFinite(ml) || ml <= 0) return;
  try {
    const r = await ctx.api(nurl('/drinks'), { method: 'POST', body: JSON.stringify({ ml, kind: drinkKind() }) });
    applyDrinks(r);
    forgetRanges();
    ctx.toast(`Logged ${ml} ml.`);
    paint();
  } catch (e) { ctx.toast(e.message, true); }
}

async function undoDrink(last) {
  if (!last?.id) return;
  try {
    const r = await ctx.api(nurl(`/drinks/${encodeURIComponent(last.id)}`), { method: 'DELETE' });
    applyDrinks(r);
    forgetRanges();
    ctx.toast('Took that one back off.');
    paint();
  } catch (e) { ctx.toast(e.message, true); }
}

/** The drink routes answer with today's drinks and today's totals; fold them in. */
function applyDrinks(r) {
  if (!r || !S.data) return;
  if (Array.isArray(r.drinks)) {
    S.data.drinks = r.drinks;
    // Meals shows the same drinks, so keep its copy of today in step. With a
    // filter on, the next refresh is the honest answer and this leaves it alone.
    if (Array.isArray(S.data.drinkLog) && !S.filters.from && !S.filters.to && !S.filters.type) {
      const key = localDay(new Date());
      S.data.drinkLog = [...r.drinks, ...S.data.drinkLog.filter((d) => localDay(new Date(d.at)) !== key)]
        .sort((a, b) => String(b.at).localeCompare(String(a.at)));
    }
  }
  if (r.today) S.data.today = r.today;
  if (Array.isArray(r.customDrinks)) S.data.customDrinks = r.customDrinks;
}

async function saveGoal() {
  const el = panel.querySelector('#hyGoal');
  const goalMl = clamp(el?.value, 8000);
  if (goalMl < 500) return ctx.toast('Set a goal of at least 500 ml.', true);
  try {
    const r = await ctx.api('/api/nutrition/goal', { method: 'PATCH', body: JSON.stringify({ goalMl }) });
    if (S.data) {
      S.data.goalMl = n0(r?.goalMl) || goalMl;
      if (S.data.today) S.data.today.goalMl = S.data.goalMl;
    }
    forgetRanges();
    ctx.toast('Goal saved.');
    await refresh();
  } catch (e) { ctx.toast(e.message, true); }
}

async function loadHydroChart() {
  const box = panel.querySelector('#hyChart');
  if (!box) return;
  try {
    if (!S.an[7]) S.an[7] = await ctx.api(nurl('/analytics', { days: 7 }));
    const days = Array.isArray(S.an[7].days) ? S.an[7].days : [];
    const goal = n0(S.data?.today?.goalMl) || n0(S.data?.goalMl) || 2500;
    if (!panel.contains(box)) return;
    box.innerHTML = days.length
      ? `${barChart(days, counted, { goal, label: 'counted ml', w: chartW(true) })}
         <p class="sub">Counted millilitres: water in full, everything else at eighty percent. The dashed line is the goal you set.</p>`
      : '<p class="sub">No drinks logged in the last seven days.</p>';
  } catch (e) { box.innerHTML = `<p class="sub">${esc(e.message)}</p>`; }
}

/* -------------------------------------------------------- drink reminders */

const REM_KEY = 'hb.nutrition.reminders';
const REM_EVERY = 2 * 60 * 60 * 1000;
const REM_START = 8, REM_END = 21;

function remState() {
  try { return JSON.parse(localStorage.getItem(REM_KEY) || 'null') || { on: false, last: 0, mode: 'toast' }; }
  catch { return { on: false, last: 0, mode: 'toast' }; }
}
function saveRem(s) { try { localStorage.setItem(REM_KEY, JSON.stringify(s)); } catch { /* private mode, reminders stay off */ } }

async function toggleReminders() {
  const s = remState();
  if (s.on) { saveRem({ ...s, on: false }); ctx.toast('Drink reminders off.'); paint(); return; }
  let mode = 'toast';
  // Permission is only ever asked for here, when the player turns the toggle on.
  if ('Notification' in window) {
    let perm = Notification.permission;
    if (perm === 'default') { try { perm = await Notification.requestPermission(); } catch { perm = 'denied'; } }
    if (perm === 'granted') mode = 'notify';
  }
  saveRem({ on: true, last: Date.now(), mode });
  startReminders();
  ctx.toast(mode === 'notify'
    ? 'Drink reminders on, about every two hours while you are awake.'
    : 'Drink reminders on. They show inside the app.');
  paint();
}

function startReminders() {
  if (remTimer) return;
  remTimer = setInterval(tickReminders, 5 * 60 * 1000);
}

function tickReminders() {
  const s = remState();
  if (!s.on) return;
  const h = new Date().getHours();
  if (h < REM_START || h >= REM_END) return;
  if (Date.now() - n0(s.last) < REM_EVERY) return;
  saveRem({ ...s, last: Date.now() });
  // The nudge they asked for, and nothing else. No claim about what water does
  // to their body, and no amount put in front of them.
  const msg = 'Your water reminder. Log whatever you drink and it counts toward the goal you set.';
  if (s.mode === 'notify' && 'Notification' in window && Notification.permission === 'granted') {
    try { new Notification('Hooper Buddy', { body: msg }); return; } catch { /* fall through to the toast */ }
  }
  ctx.toast(msg);
}

/* ============================================================ 4. ANALYTICS
 * A chosen range, and everything the log can honestly say about it. Nothing
 * here is scored and nothing is compared with anyone else. */

const rangeCard = () => `
  <div class="card">
    <h3>How far back</h3>
    <div class="ntabs" id="anRange" style="margin-bottom:0">${RANGES.map(([d, label]) =>
      `<button data-days="${d}" class="${S.range === d ? 'on' : ''}">${esc(label)}</button>`).join('')}</div>
  </div>`;

function bindRange() {
  bind('#anRange button', 'click', (e) => {
    const days = Number(e.currentTarget.dataset.days);
    if (!days || days === S.range) return;
    S.range = days;
    paint();
  });
}

async function paintAnalytics() {
  const days = S.range;
  const loading = (msg) => { panel.innerHTML = `${rangeCard()}<div class="card"><h3>Analytics</h3><p class="sub">${esc(msg)}</p></div>`; bindRange(); };
  loading(`Adding up your last ${days} days.`);

  let a = S.an[days];
  if (!a) {
    try { a = S.an[days] = await ctx.api(nurl('/analytics', { days })); }
    catch (e) { if (S.tab === 'analytics' && S.range === days) loading(e.message); return; }
  }
  if (S.tab !== 'analytics' || S.range !== days) return;

  // The meals themselves, for the times of day and the names. The analytics
  // route hands back day totals, not the entries, so this is a second read and
  // it deliberately ignores the Meals tab filter.
  if (!S.rangeMeals[days]) {
    const from = a.days?.[0]?.date || '';
    try {
      const r = await ctx.api(nurl('', from ? { from } : null));
      S.rangeMeals[days] = Array.isArray(r.meals) ? r.meals : [];
    } catch { S.rangeMeals[days] = []; }
  }
  if (S.tab !== 'analytics' || S.range !== days) return;
  if (!S.sessions) await loadSessions();
  if (S.tab !== 'analytics' || S.range !== days) return;

  const rows = Array.isArray(a.days) ? a.days : [];
  const logged = rows.filter((d) => n0(d.calories) > 0 || n0(d.ml) > 0 || n0(d.meals) > 0).length;
  if (logged < 3) {
    panel.innerHTML = `${rangeCard()}
      <div class="card"><h3>Analytics</h3>
        <p class="sub">Not enough logged yet. In this range you have ${logged} day${logged === 1 ? ' with food or drink on it' : 's with food or drink on them'}, and the charts start at three.</p>
        <p class="sub" style="margin-top:8px">Log a couple more days and the charts fill in on their own. Nothing here is a target, it is only your own log drawn back at you.</p>
      </div>`;
    bindRange();
    return;
  }

  const meals = S.rangeMeals[days] || [];
  const totals = a.totals || {}, averages = a.averages || {}, byType = a.byType || {};
  const goal = n0(a.goalMl) || n0(S.data?.today?.goalMl) || n0(S.data?.goalMl) || 2500;
  const full = chartW(false), half = chartW(true);
  const avgCal = n0(averages.calories), avgPro = n0(averages.protein);
  const typeSlices = Object.entries(byType).filter(([, v]) => n0(v) > 0)
    .map(([k, v], i) => ({ value: n0(v), label: k, colour: ACCENTS[i % ACCENTS.length] }));
  const gram = n0(totals.protein) + n0(totals.carbs) + n0(totals.fat);
  const macroSlices = SPLIT.map(([k, label, colour]) => ({ value: n0(totals[k]), colour, label }));

  panel.innerHTML = `
    ${rangeCard()}

    <div class="card">
      <h3>Calories over time</h3>
      ${lineChart(rows, 'calories', { label: 'calories', w: full, avg: avgCal, avgLabel: 'your average' })}
      <p class="sub">Every day in the range, your own average across it as the dashed line. An empty day is a day you logged nothing.</p>
    </div>

    <div class="card">
      <h3>Macros over time</h3>
      ${stackChart(rows, { w: full, label: 'grams' })}
      <div class="nlegend">${SPLIT.map(([, label, colour]) =>
        `<span><i style="background:${colour}"></i>${esc(label)}</span>`).join('')}</div>
    </div>

    <div class="ngrid">
      <div class="card">
        <h3>Protein a day</h3>
        ${barChart(rows, 'protein', { label: 'grams', w: half, colour: 'var(--hot)', avg: avgPro, avgLabel: 'your average' })}
        <p class="sub">Your own average across this range is ${fmt(avgPro)} g a day.</p>
      </div>
      <div class="card">
        <h3>Water a day</h3>
        ${barChart(rows, counted, { label: 'counted ml', w: half, goal })}
        <p class="sub">Counted millilitres against the ${fmt(goal)} ml goal you set yourself. Water counts fully, everything else eighty percent.</p>
      </div>
    </div>

    <div class="card">
      <h3>When you actually eat</h3>
      ${hourChart(meals, { w: full })}
      <p class="sub">${timingLine(meals)}</p>
    </div>

    <div class="ngrid">
      <div class="card">
        <h3>By meal type</h3>
        ${typeSlices.length ? `
          <div class="nsplitwrap">
            ${donutSvg(typeSlices, { size: 132, stroke: 18, mid: fmt(totals.meals), sub: 'LOGGED', label: 'What share of your logged meals each type is' })}
            <div class="nlegend col">${typeSlices.map((s) =>
              `<span><i style="background:${s.colour}"></i>${esc(s.label)} ${fmt(s.value)}</span>`).join('')}</div>
          </div>` : '<p class="sub">Nothing logged by type yet.</p>'}
      </div>
      <div class="card">
        <h3>Macro split</h3>
        ${gram > 0 ? `
          <div class="nsplit">${macroSlices.map((s) =>
            `<i style="width:${((s.value / gram) * 100).toFixed(1)}%;background:${s.colour}"></i>`).join('')}</div>
          <div class="nlegend">${macroSlices.map((s) =>
            `<span><i style="background:${s.colour}"></i>${esc(s.label)} ${fmt(s.value)} g, ${Math.round((s.value / gram) * 100)}%</span>`).join('')}</div>
          <p class="sub">The share of the grams you logged across these ${days} days.</p>` : '<p class="sub">No macros logged yet.</p>'}
        <h3 style="margin-top:20px">A day on average</h3>
        <div class="grid-stats">
          <div class="stat"><b>${fmt(averages.calories)}</b><small>calories</small></div>
          <div class="stat"><b>${fmt(averages.protein)}</b><small>protein g</small></div>
          <div class="stat"><b>${fmt(averages.carbs)}</b><small>carbs g</small></div>
          <div class="stat"><b>${fmt(averages.fat)}</b><small>fat g</small></div>
          <div class="stat"><b>${fmt(averages.ml)}</b><small>ml drunk</small></div>
          <div class="stat"><b>${fmt(averages.meals)}</b><small>meals</small></div>
        </div>
      </div>
    </div>

    <div class="card">
      <h3>What you log most</h3>
      ${topFoods(meals, full)}
    </div>

    <div class="card">
      <h3>Logging days and training days</h3>
      ${trainingGrid(rows, full)}
    </div>

    <div class="card">
      <h3>Streaks</h3>
      ${streakTiles(S.data?.streak)}
      <p class="nplain">All of this is your own log, nothing else. It is here to help you fuel your training, it is not a diet and it is not health advice.</p>
    </div>`;

  bindRange();
}

/**
 * A training day is a day they actually shot. The two example sessions every
 * new account ships with are left out, and so is a session with no attempts in
 * it, which is the same rule store-kv.js uses for the journal grid and
 * worker/nutrition.js uses for what the model reads. Without it a player who
 * has never trained is told they trained twice.
 */
async function loadSessions() {
  try {
    const list = await ctx.api('/api/sessions');
    const real = (Array.isArray(list) ? list : []).filter((s) => s && !s.demo && n0(s.attempts) > 0);
    S.sessions = { list: real, err: null };
  } catch (e) { S.sessions = { list: [], err: e.message }; }
}

/** When their food actually lands, said as a fact and nothing more. */
function timingLine(meals) {
  if (!meals.length) return 'Nothing logged in this range yet, so there is no pattern to show.';
  const hours = new Array(24).fill(0);
  for (const m of meals) { const h = new Date(m.at).getHours(); if (h >= 0 && h < 24) hours[h] += 1; }
  let top = 0;
  for (let i = 1; i < 24; i++) if (hours[i] > hours[top]) top = i;
  const first = hours.findIndex((v) => v > 0);
  let lastH = 0;
  for (let i = 0; i < 24; i++) if (hours[i] > 0) lastH = i;
  return `Your busiest hour is around ${hourLabel(top)}. The earliest thing you logged was around ${hourLabel(first < 0 ? top : first)} and the latest around ${hourLabel(lastH)}.`;
}

function streakTiles(streak) {
  const entries = Object.entries(streak || {}).filter(([, v]) => typeof v === 'number');
  if (!entries.length) return '<p class="sub">No streak yet. Log two days in a row and it starts counting.</p>';
  // The route hands back consecutive days, not totals, so the labels say so.
  const LABEL = { current: 'day streak', best: 'best streak', longest: 'longest streak', days: 'days in a row',
    meals: 'days logging in a row', hydration: 'days at your water goal', water: 'days at your water goal' };
  return `<div class="grid-stats">${entries.slice(0, 6).map(([k, v]) =>
    `<div class="stat"><b>${fmt(v)}</b><small>${esc(LABEL[k] || k)}</small></div>`).join('')}</div>`;
}

/* ------------------------------------------------------------- the charts
 * Drawn by hand as inline SVG so they carry the brand tokens and read in both
 * themes. Colours go in style attributes, which is where var() works. The
 * viewBox is set to roughly the real pixel width of the card, so the labels
 * inside a chart stay the size they say they are on a phone. */

const CH = { h: 200, pad: { l: 46, r: 14, t: 18, b: 30 } };
const niceMax = (v) => { const base = Math.pow(10, Math.max(0, String(Math.round(v)).length - 2)); return Math.max(base, Math.ceil(v / base) * base); };
const valueOf = (row, key) => (typeof key === 'function' ? n0(key(row)) : n0(row?.[key]));

function chartW(half) {
  const outer = panel?.clientWidth || root?.clientWidth || 640;
  const col = half && window.innerWidth > 900 ? (outer - 14) / 2 : outer;
  return Math.min(760, Math.max(260, Math.round(col - 40)));
}

// xAt places the date labels. A bar chart puts them under the middle of each
// bar, a line chart under its point, so the two never drift apart.
function axes(w, max, days, unit, xAt) {
  const { l, r, t, b } = CH.pad;
  const y = (v) => t + (1 - v / max) * (CH.h - t - b);
  const ticks = [0, max / 2, max].map((v) => `
    <line x1="${l}" y1="${y(v).toFixed(1)}" x2="${w - r}" y2="${y(v).toFixed(1)}" style="stroke:var(--line)" stroke-width="1"></line>
    <text x="${l - 8}" y="${(y(v) + 4).toFixed(1)}" text-anchor="end" style="fill:var(--mute);font:700 11px var(--mono)">${fmt(v)}</text>`).join('');
  const marks = [0, Math.floor((days.length - 1) / 2), days.length - 1].filter((i, n, all) => all.indexOf(i) === n && i >= 0);
  const x = xAt || ((i) => l + (days.length < 2 ? (w - l - r) / 2 : (i / (days.length - 1)) * (w - l - r)));
  const labels = marks.map((i) => `<text x="${x(i).toFixed(1)}" y="${CH.h - 10}" text-anchor="middle" style="fill:var(--mute);font:700 11px var(--mono)">${esc(shortDay(String(days[i]?.date || '')))}</text>`).join('');
  // The unit sits above the top tick, clear of the date labels along the bottom.
  const unitLabel = `<text x="4" y="11" style="fill:var(--dim);font:700 10px var(--mono)">${esc(unit)}</text>`;
  return { y, x, svg: ticks + labels + unitLabel };
}

/** A label over a chart keeps a halo of the card colour, so it stays readable
 *  where it crosses a bar or a line. */
const halo = (colour) => `fill:${colour};stroke:var(--panel);stroke-width:3.5;paint-order:stroke;font:800 11px var(--mono)`;

/** The player's own average across the range, never a target. */
function avgLine(w, y, value, text) {
  if (!(value > 0)) return '';
  const { l, r } = CH.pad;
  return `<line x1="${l}" y1="${y(value).toFixed(1)}" x2="${w - r}" y2="${y(value).toFixed(1)}" style="stroke:var(--mute)" stroke-width="1.5" stroke-dasharray="5 5"></line>
    <text x="${w - r}" y="${(y(value) - 6).toFixed(1)}" text-anchor="end" style="${halo('var(--mute)')}">${esc(text)} ${fmt(value)}</text>`;
}

function lineChart(days, key, { label = '', w = 640, avg = 0, avgLabel = '' } = {}) {
  const vals = days.map((d) => valueOf(d, key));
  const max = niceMax(Math.max(1, avg, ...vals));
  const { y, x, svg } = axes(w, max, days, label);
  const pts = vals.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
  const base = (CH.h - CH.pad.b).toFixed(1);
  const area = `<path d="M${x(0).toFixed(1)},${base} L${pts.join(' L')} L${x(vals.length - 1).toFixed(1)},${base} Z" style="fill:var(--hot)" opacity=".12"></path>`;
  const dots = vals.length <= 32
    ? vals.map((v, i) => `<circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="3" style="fill:var(--hot)"><title>${esc(shortDay(days[i]?.date))}: ${fmt(v)}</title></circle>`).join('')
    : '';
  return `<svg class="nchart" viewBox="0 0 ${w} ${CH.h}" role="img" aria-label="${esc(label)} for each of the last ${days.length} days">
    ${svg}${area}
    <polyline points="${pts.join(' ')}" fill="none" style="stroke:var(--hot)" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"></polyline>
    ${dots}${avgLine(w, y, avg, avgLabel)}
  </svg>`;
}

function barChart(days, key, { goal = 0, label = '', w = 640, colour = 'var(--cool)', avg = 0, avgLabel = '' } = {}) {
  const vals = days.map((d) => valueOf(d, key));
  const max = niceMax(Math.max(1, goal, avg, ...vals));
  const span = (w - CH.pad.l - CH.pad.r) / Math.max(1, days.length);
  const bw = Math.max(2, span * 0.62);
  const { y, svg } = axes(w, max, days, label, (i) => CH.pad.l + i * span + span / 2);
  const bars = vals.map((v, i) => {
    const bx = CH.pad.l + i * span + (span - bw) / 2;
    const by = y(v), h = Math.max(0, CH.h - CH.pad.b - by);
    return `<rect x="${bx.toFixed(1)}" y="${by.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" rx="3" style="fill:${colour}"><title>${esc(shortDay(days[i]?.date))}: ${fmt(v)}</title></rect>`;
  }).join('');
  const goalLine = goal > 0 ? `
    <line x1="${CH.pad.l}" y1="${y(goal).toFixed(1)}" x2="${w - CH.pad.r}" y2="${y(goal).toFixed(1)}" style="stroke:var(--good)" stroke-width="2" stroke-dasharray="6 5"></line>
    <text x="${w - CH.pad.r}" y="${(y(goal) - 6).toFixed(1)}" text-anchor="end" style="${halo('var(--good)')}">goal ${fmt(goal)}</text>` : '';
  return `<svg class="nchart" viewBox="0 0 ${w} ${CH.h}" role="img" aria-label="${esc(label)} for each of the last ${days.length} days">
    ${svg}${bars}${goalLine}${avgLine(w, y, avg, avgLabel)}
  </svg>`;
}

/** Protein, carbs and fat stacked, so the shape of a day is visible at a glance. */
function stackChart(days, { w = 640, label = '' } = {}) {
  const per = days.map((d) => SPLIT.map(([k]) => n0(d[k])));
  const max = niceMax(Math.max(1, ...per.map((r) => r.reduce((a, b) => a + b, 0))));
  const span = (w - CH.pad.l - CH.pad.r) / Math.max(1, days.length);
  const bw = Math.max(2, span * 0.62);
  const { y, svg } = axes(w, max, days, label, (i) => CH.pad.l + i * span + span / 2);
  const bars = per.map((row, i) => {
    const bx = CH.pad.l + i * span + (span - bw) / 2;
    let top = CH.h - CH.pad.b;
    return row.map((v, j) => {
      if (v <= 0) return '';
      const h = Math.max(0, (CH.h - CH.pad.b - CH.pad.t) * (v / max));
      top -= h;
      return `<rect x="${bx.toFixed(1)}" y="${top.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" style="fill:${SPLIT[j][2]}"><title>${esc(shortDay(days[i]?.date))}: ${fmt(v)} g ${SPLIT[j][1].toLowerCase()}</title></rect>`;
    }).join('');
  }).join('');
  return `<svg class="nchart" viewBox="0 0 ${w} ${CH.h}" role="img" aria-label="Protein, carbs and fat in grams for each of the last ${days.length} days">
    ${svg}${bars}
  </svg>`;
}

/** What time of day the food actually lands, hour by hour. */
function hourChart(meals, { w = 640 } = {}) {
  const hours = new Array(24).fill(0);
  for (const m of meals) { const h = new Date(m.at).getHours(); if (h >= 0 && h < 24) hours[h] += 1; }
  const total = hours.reduce((a, b) => a + b, 0);
  if (!total) return '<p class="sub">No meals in this range yet, so there is nothing to place on a clock.</p>';
  const h = 170, pad = { l: 34, r: 12, t: 16, b: 28 };
  const max = Math.max(1, ...hours);
  const span = (w - pad.l - pad.r) / 24;
  const bw = Math.max(3, span * 0.66);
  const y = (v) => pad.t + (1 - v / max) * (h - pad.t - pad.b);
  const grid = [0, max].map((v) => `
    <line x1="${pad.l}" y1="${y(v).toFixed(1)}" x2="${w - pad.r}" y2="${y(v).toFixed(1)}" style="stroke:var(--line)" stroke-width="1"></line>
    <text x="${pad.l - 8}" y="${(y(v) + 4).toFixed(1)}" text-anchor="end" style="fill:var(--mute);font:700 11px var(--mono)">${fmt(v)}</text>`).join('');
  const bars = hours.map((v, i) => {
    if (v <= 0) return '';
    const x = pad.l + i * span + (span - bw) / 2;
    const by = y(v);
    return `<rect x="${x.toFixed(1)}" y="${by.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(0, h - pad.b - by).toFixed(1)}" rx="2" style="fill:var(--grape)"><title>${esc(hourLabel(i))}: ${v} logged</title></rect>`;
  }).join('');
  const labels = [0, 6, 12, 18, 23].map((i) =>
    `<text x="${(pad.l + i * span + span / 2).toFixed(1)}" y="${h - 9}" text-anchor="middle" style="fill:var(--mute);font:700 11px var(--mono)">${esc(hourLabel(i))}</text>`).join('');
  return `<svg class="nchart" viewBox="0 0 ${w} ${h}" role="img" aria-label="How many meals you logged in each hour of the day">
    <text x="4" y="11" style="fill:var(--dim);font:700 10px var(--mono)">meals logged</text>
    ${grid}${bars}${labels}
  </svg>`;
}

/** The names that come up most, counted from the meals in the range. */
function topFoods(meals, w) {
  const seen = new Map();
  for (const m of meals) {
    const name = String(m?.name || '').replace(/\s+/g, ' ').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const row = seen.get(key) || { name, count: 0, calories: 0 };
    row.count += 1;
    row.calories += n0(m.calories);
    seen.set(key, row);
  }
  const rows = [...seen.values()].sort((a, b) => b.count - a.count).slice(0, 8);
  if (!rows.length) return '<p class="sub">Nothing logged in this range yet.</p>';
  const rowH = 30, padT = 6, labelW = Math.min(210, Math.max(110, Math.round(w * 0.4)));
  const h = padT * 2 + rows.length * rowH;
  const max = Math.max(1, ...rows.map((r) => r.count));
  const barMax = w - labelW - 54;
  const bars = rows.map((r, i) => {
    const y = padT + i * rowH;
    const bw = Math.max(2, (r.count / max) * barMax);
    return `<text x="0" y="${y + 18}" style="fill:var(--ink);font:700 12px var(--mono)">${esc(cut(r.name, Math.floor(labelW / 6.6)))}</text>
      <rect x="${labelW}" y="${y + 7}" width="${bw.toFixed(1)}" height="15" rx="4" style="fill:var(--hot)" opacity=".85"></rect>
      <text x="${(labelW + bw + 8).toFixed(1)}" y="${y + 19}" style="fill:var(--mute);font:800 11px var(--mono)">${r.count}x</text>`;
  }).join('');
  return `<svg class="nchart" viewBox="0 0 ${w} ${h}" role="img" aria-label="The meal names you logged most often in this range">${bars}</svg>
    <p class="sub">Counted by the name you gave the meal, most logged first.</p>`;
}

/**
 * One cell a day: did they log food, did they drink, did they train. The
 * training days come from their own sessions, so a day with practice on it is
 * visible next to a day they logged nothing.
 */
function trainingGrid(rows, w) {
  const sessions = S.sessions?.list || [];
  const trained = new Set(sessions.map((s) => {
    const t = Date.parse(s?.startedAt);
    return Number.isFinite(t) ? localDay(new Date(t)) : '';
  }).filter(Boolean));
  const cell = 15, gap = 5;
  const cols = Math.max(7, Math.floor((w + gap) / (cell + gap)));
  const h = Math.ceil(rows.length / cols) * (cell + gap) + 2;
  const cells = rows.map((d, i) => {
    const x = (i % cols) * (cell + gap);
    const y = Math.floor(i / cols) * (cell + gap);
    const food = n0(d.calories) > 0 || n0(d.meals) > 0;
    const water = n0(d.ml) > 0;
    const train = trained.has(d.date);
    const fill = food ? 'var(--hot)' : water ? 'var(--cool)' : 'var(--panel2)';
    const note = [food ? 'logged food' : '', water ? 'drank something' : '', train ? 'trained' : '']
      .filter(Boolean).join(', ') || 'nothing logged';
    return `<rect x="${x}" y="${y}" width="${cell}" height="${cell}" rx="4" style="fill:${fill};stroke:${train ? 'var(--good)' : 'var(--line)'}" stroke-width="${train ? 2 : 1}" opacity="${food || water ? '.9' : '1'}"><title>${esc(shortDay(d.date))}: ${esc(note)}</title></rect>`;
  }).join('');
  const trainedInRange = rows.filter((d) => trained.has(d.date)).length;
  const bothDays = rows.filter((d) => trained.has(d.date) && (n0(d.calories) > 0 || n0(d.meals) > 0)).length;
  return `<svg class="nchart" viewBox="0 0 ${cols * (cell + gap)} ${h}" role="img" aria-label="Every day in the range, whether you logged food, drank something or trained">${cells}</svg>
    <div class="nlegend" style="margin-top:12px">
      <span><i style="background:var(--hot)"></i>Food logged</span>
      <span><i style="background:var(--cool)"></i>Only drinks</span>
      <span><i style="background:var(--panel2);border:1px solid var(--line)"></i>Nothing logged</span>
      <span><i style="background:transparent;border:2px solid var(--good)"></i>A training session</span>
    </div>
    <p class="sub" style="margin-top:10px">${S.sessions?.err
      ? `Your sessions did not load, so training days are missing here: ${esc(S.sessions.err)}`
      : trainedInRange
        ? `You trained on ${trainedInRange} of these ${rows.length} days, and logged food on ${bothDays} of those.`
        : 'No training sessions in this range, so nothing is ringed.'}</p>`;
}

/* ========================================================== 5. AI INSIGHTS */

function paintInsights() {
  const i = S.insights;
  const facts = insightFacts();
  panel.innerHTML = `
    <div class="card">
      <h3>What your companion sees in your food</h3>
      <p class="sub">It reads your last fourteen days: the meals and drinks you logged, the sleep you put in Health, and your recent training sessions. Then it tells you what it notices, in its own words.</p>
      <div class="nread">
        <span class="nread-h">What it has to read</span>
        <div class="log">${facts.map((f) => `<span class="chip">${esc(f)}</span>`).join('')}</div>
      </div>
      <button class="btn primary" id="aiGo" style="margin-top:14px">${i ? 'Read it again' : 'Read my last two weeks'}</button>
      ${S.insightsErr ? `<p class="sub" style="margin-top:12px">${esc(S.insightsErr)}</p>` : ''}
      ${i ? `
        <div class="nreport">
          <div class="nreport-h"><span class="pill hot">Your read</span><span>${esc(whenText(i.at))}</span></div>
          ${String(i.text || '').split(/\n{2,}/).filter((p) => p.trim()).map((para) =>
            `<p>${esc(para.trim()).replace(/\n/g, '<br>')}</p>`).join('')}
        </div>
        <small class="nstamp">Your companion writes one of these a day, so reading it again today gives you the same words back.</small>` : ''}
      <p class="nplain">This is about fuelling your training. It is not medical advice, not diet advice and never a target. Anything about your health, your weight or how you feel goes to a parent, a coach or a doctor.</p>
    </div>`;
  bind('#aiGo', 'click', loadInsights);
  warmInsightFacts();
}

/**
 * The counts beside the read are fetched once, in the background, and only
 * once: a failed call leaves the chips shorter rather than trying forever.
 */
async function warmInsightFacts() {
  if (S.factsTried) return;
  S.factsTried = true;
  if (!S.an[14]) { try { S.an[14] = await ctx.api(nurl('/analytics', { days: 14 })); } catch { /* the chips just show less */ } }
  if (!S.sessions) await loadSessions();
  if (S.tab === 'insights') paintInsights();
}

/** What is actually in the window the route reads, counted from the player's own
 *  log. Nothing here is invented and nothing comes back from the model. */
function insightFacts() {
  const a = S.an[14];
  const out = ['The last 14 days'];
  if (a) {
    const rows = Array.isArray(a.days) ? a.days : [];
    const live = rows.filter((d) => n0(d.calories) > 0 || n0(d.ml) > 0 || n0(d.meals) > 0).length;
    out.push(`${fmt(a.totals?.meals)} meals logged`, `${fmt(a.totals?.ml)} ml drunk`, `${live} day${live === 1 ? '' : 's'} with something on them`);
  }
  if (S.sessions && !S.sessions.err) {
    const start = new Date(); start.setDate(start.getDate() - 13);
    const startKey = localDay(start);
    const sess = S.sessions.list.filter((s) => {
      const t = Date.parse(s?.startedAt);
      return Number.isFinite(t) && localDay(new Date(t)) >= startKey;
    }).length;
    out.push(`${sess} training session${sess === 1 ? '' : 's'}`);
  }
  out.push('Plus the sleep you logged in Health');
  return out;
}

function whenText(at) {
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return 'just now';
  const d = new Date(t);
  const today = localDay(d) === localDay(new Date());
  return `Written ${today ? 'today' : d.toLocaleDateString()} at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

async function loadInsights() {
  const btn = panel.querySelector('#aiGo');
  if (btn) { btn.disabled = true; btn.textContent = 'Reading your last two weeks'; }
  S.insightsErr = null;
  // The counts beside the read come from the same fourteen days the route uses.
  if (!S.an[14]) { try { S.an[14] = await ctx.api(nurl('/analytics', { days: 14 })); } catch { /* the chips just show less */ } }
  if (!S.sessions) await loadSessions();
  try {
    const r = await ctx.api(nurl('/insights'));
    S.insights = { text: String(r?.text || ''), at: r?.at || new Date().toISOString() };
  } catch (e) { S.insightsErr = e.message; }
  if (S.tab === 'insights') paintInsights();
}
